import store from "../../../redux/store";
import notice from "../../utils/notice";
/* eslint-disable no-throw-literal */
export function getToken() {
  return store.getState().auth.authToken;
}

export function getParams(method, body = null) {
  return {
    method: method,
    body: body,
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
  };
}

export function getMultipartParams(method, body = null) {
  return {
    method: method,
    body: body,
    headers: {
      Authorization: getToken(),
    },
  };
}

export const handleFormData = (data) => {
  const formData = new FormData();

  for (const name in data) {
    // Skip null/undefined so they aren't serialized as the literal
    // strings "null"/"undefined" — the backend casts those to 0 for
    // integer columns (e.g. an unselected optional team_id), which then
    // trips a foreign-key constraint. An absent param lets the backend
    // fall back to its own default instead.
    if (data[name] === undefined || data[name] === null) continue;
    if (Array.isArray(data[name]))
      data[name].forEach((item) =>
        Object.keys(item).forEach((key) =>
          formData.append(`${name}[][${key}]`, item[key])
        )
      );
    else formData.append(name, data[name]);
  }

  return formData;
};

// RETURN THE STRING FOR THE PARAMS OF A GET REQUEST FROM AN OBJECT
export const queryParamsFromObject = (obj) =>
  Object.entries(obj)
    .map((x) => x.join("="))
    .join("&");

// On 422, handleResponse throws the raw JSON body, which Rails
// builds from ActiveModel::Errors#full_messages — an array of
// human-readable strings ("Name has already been taken", etc.).
// On other failures it throws { errors: response.statusText, body }
// where `errors` is just a status line ("Bad Request") and `body` is
// the parsed response. Use this helper in CRUD catch blocks to surface
// the real reason in the toast when it's available, and fall back to
// the generic message on transport errors so admins don't see
// "Internal Server Error" verbatim.
//
// Reading only the top-level `errors` array missed every refusal that
// isn't a 422, because on those `errors` holds the status line and the
// reason sits in `body`. The API states a refusal in three keys, by
// layer: `errors` (422 validations, 401 auth), `error` (the 403 role
// and school-scope guards), `message` (any `raise("…")` in a
// controller, as a 400). All three arrived and none were printed — a
// coach refused an alert send saw only "Could not send the alert",
// which is how a permission rule read as a server fault for a week.
export const surfaceServerErrors = (e, fallback) => {
  if (Array.isArray(e?.errors) && e.errors.length) {
    return e.errors.join(", ");
  }

  const body = e?.body;
  // A non-JSON body (Rails' 500 HTML page) comes through as a raw
  // string — never printable, always the fallback.
  if (!body || typeof body !== "object") {
    return fallback;
  }

  if (Array.isArray(body.errors) && body.errors.length) {
    return body.errors.join(", ");
  }

  // Rails' own public exception page answers 404s and 5xx as
  // { status, error: "Not Found" | "Internal Server Error" }. Our
  // controllers never put `status` inside the body, so its presence
  // marks the framework page rather than a refusal anyone wrote.
  if (typeof body.status === "number" && body.error !== undefined) {
    return fallback;
  }

  const stated = [body.errors, body.error, body.message].find(
    (value) => typeof value === "string" && value.trim() !== ""
  );

  return stated ? stated.trim() : fallback;
};

export async function handleResponse(response) {
  if (!response.ok) {
    if (response.status === 401) {
      store.dispatch({ type: "[Logout] Action" });
      notice("Session expired. Please log in again");
      throw { errors: "Session expired" };
    } else if (response.status === 422) {
      throw await response.json();
    }

    // Read the body exactly once. The old `try json() / catch text()`
    // pattern crashed with "Failed to execute 'text' on 'Response':
    // body stream already read" whenever the backend returned a
    // non-JSON body (Rails 500 HTML page, empty body, plain text) —
    // the failed json() call had already consumed the stream, so
    // the text() fallback threw, and that throw clobbered the
    // structured `throw { errors, body }` below. The result was an
    // uncaught TypeError instead of the toast-friendly error, which
    // is what Janaya hit on /users/staff.
    //
    // Read text once, then try to parse as JSON. Falls back to the
    // raw string for non-JSON responses (so we still have something
    // useful in the thrown body), or to the empty object on a body
    // we couldn't read at all.
    let body = {};
    try {
      const text = await response.text();
      if (text) {
        try {
          body = JSON.parse(text);
        } catch {
          body = text;
        }
      }
    } catch {
      // network error reading the body — leave body = {} and throw
      // the statusText anyway so the caller's toast still fires.
    }

    throw { errors: response.statusText, body };
  }

  if (response.status === 204) {
    return {};
  }

  try {
    const text = await response.text();

    if (!text) {
      return {};
    }

    try {
      return JSON.parse(text);
    } catch {
      return { raw: text };
    }
  } catch {
    return {};
  }
}

export const handleError = (error) => {
  console.log(error);
  throw error;
};
